<template>
    <kiosk-layout>
        <div>
            <page-callout section="kiosk-inbound-text-links" class="mb-4" />
            <page-header>
                Links

                <template v-if="props.enabled" v-slot:actions>
                    <vx-button href="/kiosk/inbound-text/links/new" size="lg" data-test="new-link">
                        New Link <font-awesome-icon widthAuto :icon="faCirclePlus" />
                    </vx-button>
                </template>
            </page-header>

            <vx-alert v-if="!props.enabled" color="info" :closeable="false" data-test="disabled">
                Inbound text links aren't available in this environment.
            </vx-alert>

            <template v-else>
                <kiosk-inbound-text-links-controls
                    v-model="fetchParams"
                    @apply="applyFilters"
                    @reset="resetFilters"
                ></kiosk-inbound-text-links-controls>

                <page-loading v-if="loading && !list.length"></page-loading>

                <empty-state v-else-if="!list.length && fetchParams.query" @reset="resetFilters" />

                <empty-state v-else-if="!list.length">
                    No links yet.
                    <vx-button href="/kiosk/inbound-text/links/new" size="lg">
                        New Link <font-awesome-icon widthAuto :icon="faCirclePlus" />
                    </vx-button>
                </empty-state>

                <div v-else>
                    <kiosk-inbound-text-links-table>
                        <kiosk-inbound-text-link-row
                            v-for="link in list"
                            :key="link.id"
                            :modelValue="link"
                            @deleted="removeLink(link.id)"
                        ></kiosk-inbound-text-link-row>
                    </kiosk-inbound-text-links-table>

                    <div v-if="fetchParams.cursor" class="mt-8">
                        <vx-button
                            @click="loadData()"
                            color="muted-dark"
                            size="xl"
                            class="grow-0"
                            data-test="load-more-btn"
                            :loading="loading"
                        >
                            Load More
                            <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
                        </vx-button>
                    </div>
                </div>
            </template>
        </div>
    </kiosk-layout>
</template>

<script lang="ts" setup>
import { faCirclePlus, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxButton } from '@voxie/frontend-components';
import EmptyState from '~/components/general/EmptyState.vue';
import PageCallout from '~/components/general/PageCallout.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import KioskInboundTextLinkRow from '~/components/kiosk/kiosk-inbound-text/list/KioskInboundTextLinkRow.vue';
import KioskInboundTextLinksControls from '~/components/kiosk/kiosk-inbound-text/list/KioskInboundTextLinksControls.vue';
import KioskInboundTextLinksTable from '~/components/kiosk/kiosk-inbound-text/list/KioskInboundTextLinksTable.vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { useToasted } from '~/composables/useToasted';
import { adminInboundTextClient } from '~/services';
import type { InboundTextLink, InboundTextLinkListParams } from '~/services/adminInboundTextClient.types';

const props = withDefaults(defineProps<{ enabled?: boolean }>(), { enabled: false });

const toasted = useToasted();

const defaultFilters = (): InboundTextLinkListParams => ({
    query: '',
    cursor: null,
});

const { loading, list, fetchParams, fetchRecords } = useCursorPagination<InboundTextLink, InboundTextLinkListParams>();

const listCancelKey = Symbol('kiosk-inbound-text-links');

const loadData = () => {
    fetchRecords(adminInboundTextClient.getLinks(fetchParams.value, { cancelKey: listCancelKey }));
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };
    loadData();
};

const resetFilters = () => {
    fetchParams.value = defaultFilters();
    loadData();
};

const removeLink = (id: number) => {
    list.value = list.value.filter((link) => link.id !== id);
    toasted.success('Link deleted');
};

fetchParams.value = defaultFilters();

if (props.enabled) {
    loadData();
}
</script>
